import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  APP_STORE_URL,
  APP_DOWNLOAD_BANNER_DISMISSED_KEY,
} from '../../constants/appLinks';

const BANNER_HEIGHT_VAR = '--app-download-banner-height';

function wasBannerDismissed() {
  try {
    return localStorage.getItem(APP_DOWNLOAD_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistBannerDismissed() {
  try {
    localStorage.setItem(APP_DOWNLOAD_BANNER_DISMISSED_KEY, '1');
  } catch {}
}

function isBannerRoute(pathname) {
  if (pathname === '/') return true;
  if (pathname.startsWith('/customer-dashboard')) return true;
  if (pathname.startsWith('/photographer-dashboard')) return true;
  return false;
}

const AppStoreButton = ({ onClick }) => (
  <a
    href={APP_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    aria-label="حمّل من App Store"
    className="inline-flex items-center gap-1.5 shrink-0 rounded-xl bg-black text-white h-8 sm:h-9 px-2.5 border border-white/15 shadow-lg shadow-black/25 hover:scale-[1.03] hover:shadow-xl active:scale-[0.98] transition-all duration-200"
  >
    <svg width={14} height={17} viewBox="0 0 814 1000" fill="white" aria-hidden>
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.8 0 663.6 0 541.8c0-207.2 135.1-316.7 268.1-316.7 70.5 0 129.3 46.3 173.7 46.3 42.8 0 109.7-49 189.2-49 30.5 0 108.2 2.6 168.9 75.4zm-225.1-191.7c31.8-38.1 54.5-91.5 54.5-145 0-7.1-.6-14.3-1.9-20.7-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 85.8-55.1 140 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 134.1-69.5z" />
    </svg>
    <div className="flex flex-col items-start leading-none">
      <span className="text-[7px] sm:text-[8px] opacity-75">Download on the</span>
      <span className="text-[11px] sm:text-xs font-bold tracking-tight">App Store</span>
    </div>
  </a>
);

/**
 * بانر تحميل تطبيق مشهد — ويب فقط، أعلى الصفحة (فوق الهيدر).
 * يُخفى نهائياً بعد الإغلاق (✕) أو الضغط على App Store.
 */
export default function AppDownloadBanner() {
  const { pathname } = useLocation();
  const bannerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform() || !isBannerRoute(pathname)) {
      setVisible(false);
      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
      return;
    }
    setVisible(!wasBannerDismissed());
  }, [pathname]);

  const syncBannerHeight = useCallback(() => {
    const el = bannerRef.current;
    const height = visible && el ? el.offsetHeight : 0;
    if (height > 0) {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${height}px`);
    } else {
      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
      return undefined;
    }

    syncBannerHeight();
    const el = bannerRef.current;
    if (!el) return undefined;

    const ro = new ResizeObserver(syncBannerHeight);
    ro.observe(el);
    window.addEventListener('resize', syncBannerHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncBannerHeight);
      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [visible, syncBannerHeight]);

  const dismiss = useCallback(() => {
    persistBannerDismissed();
    setVisible(false);
    document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
  }, []);

  const handleAppStoreClick = useCallback(() => {
    persistBannerDismissed();
    setVisible(false);
    document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
  }, []);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 inset-x-0 z-[60] w-full"
      dir="rtl"
      role="region"
      aria-label="تحميل تطبيق مشهد"
    >
      <div
        className="relative w-full overflow-hidden border-b border-white/10"
        style={{
          background:
            'linear-gradient(105deg, #0f172a 0%, color-mix(in srgb, var(--color-primary, #2563eb) 85%, #0f172a) 45%, #1e293b 100%)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.12) 0%, transparent 50%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.08) 0%, transparent 45%)',
          }}
        />

        <div className="relative w-full px-11 sm:px-14 py-2.5 sm:py-3">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 w-full max-w-none">
            <p className="text-white text-xs sm:text-sm font-medium text-center leading-snug">
              حمّل تطبيق <span className="font-bold">مشهد</span> لتجربة أفضل
            </p>
            <AppStoreButton onClick={handleAppStoreClick} />
          </div>

          <button
            type="button"
            onClick={dismiss}
            className="absolute top-1/2 -translate-y-1/2 left-3 sm:left-4 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors"
            aria-label="إغلاق"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
